// Improvements — v1.0.0 Phase 3 (Lease-Leverage Module)
//
// Per-lead capital improvement tracker. Reads from
// improvements_with_current_value (server-side straight-line
// depreciation computed at read time via CURRENT_DATE), so current_value
// stays fresh without any client-side math. Total Improvement Value
// (book) is the sum of current_value across all assets — the master
// lease values improvements at the greater of FMV or this figure.
//
// Same gate pattern as LeaseScenarios: if the lead has no lease_terms
// row, render <Navigate> to /leads with an error toast. The
// improvements table is independent of lease_terms in the schema, but
// improvements are spec'd for lease-leverage deals only.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { getLeaseTerms } from "../lib/leaseTerms";
import {
  createImprovement,
  deleteImprovement,
  getImprovements,
  updateImprovement,
} from "../lib/improvements";

// ---- options ----
const CATEGORY_OPTIONS = [
  { value: "site_prep",   label: "Site Prep"   },
  { value: "amenities",   label: "Amenities"   },
  { value: "utilities",   label: "Utilities"   },
  { value: "structures",  label: "Structures"  },
  { value: "landscaping", label: "Landscaping" },
  { value: "other",       label: "Other"       },
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORY_OPTIONS.map((o) => [o.value, o.label]));

// ---- styles (matches LeaseScenarios + /leads visual language) ----
const page = { background: "#f3f4f6", minHeight: "100vh", padding: "40px 20px" };
const container = { maxWidth: 1280, margin: "0 auto" };
const card = {
  background: "#ffffff",
  borderRadius: 16,
  padding: 22,
  boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
};
const headerRow = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  marginBottom: 8,
  flexWrap: "wrap",
  gap: 12,
};
const primaryButton = {
  background: "#1d4ed8",
  color: "#fff",
  border: "none",
  borderRadius: 12,
  padding: "10px 18px",
  fontSize: 15,
  fontWeight: 700,
  cursor: "pointer",
};
const secondaryButton = {
  background: "#fff",
  color: "#1d4ed8",
  border: "1px solid #93c5fd",
  borderRadius: 12,
  padding: "8px 14px",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  textDecoration: "none",
  display: "inline-block",
};
const dangerButton = {
  background: "#fff",
  color: "#b91c1c",
  border: "1px solid #fecaca",
  borderRadius: 10,
  padding: "6px 12px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};
const editButton = {
  background: "#fff",
  color: "#1d4ed8",
  border: "1px solid #93c5fd",
  borderRadius: 10,
  padding: "6px 12px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};
const inputStyle = {
  border: "1px solid #d1d5db",
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 15,
  width: "100%",
  boxSizing: "border-box",
  background: "#fff",
};
const labelStyle = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "#374151",
  marginBottom: 6,
};

// Category pill — softer than status pills on /leads.
const categoryChip = {
  background: "#eef2ff",
  color: "#3730a3",
  fontWeight: 600,
  fontSize: 11,
  letterSpacing: 0.5,
  padding: "4px 8px",
  borderRadius: 8,
  textTransform: "uppercase",
  display: "inline-block",
  whiteSpace: "nowrap",
};

// ---- formatters ----
function usd(n) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  return `$${Math.round(Number(n)).toLocaleString()}`;
}
function shortDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

// ---- the page ----
export default function Improvements() {
  const { leadId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  // Declarative redirect — same pattern as LeaseScenarios, so we don't
  // race with the render cycle for the no-lease-terms case.
  const [redirectMessage, setRedirectMessage] = useState(null);
  const [leaseTerms, setLeaseTerms] = useState(null);
  const [improvements, setImprovements] = useState([]);
  const [modalState, setModalState] = useState({ open: false, editing: null });
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setPageError("");

    // Gate: lease_terms must exist. Two failure modes collapse to the
    // same redirect because get-lease-terms is user-scoped — bogus
    // UUID, unowned lead, and lease-less lead all surface as null.
    const { data: termsData, error: termsErr } = await getLeaseTerms(leadId);
    if (termsErr || !termsData?.lease_terms) {
      setRedirectMessage(
        "This lead has no lease structure yet. Add it from the New Lead modal or edit the lead first."
      );
      return;
    }
    setLeaseTerms(termsData.lease_terms);

    const { data: impData, error: impErr } = await getImprovements(leadId);
    if (impErr) {
      setPageError(impErr.message || "Could not load improvements.");
      setLoading(false);
      return;
    }
    setImprovements(impData?.improvements || []);
    setLoading(false);
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  // Sum of current_value across all improvements. Server returns numeric
  // strings from numeric columns; coerce and skip nulls.
  const totalCurrentValue = useMemo(() => {
    return improvements.reduce((acc, imp) => {
      const v = imp.current_value;
      if (v === null || v === undefined) return acc;
      const n = Number(v);
      return Number.isFinite(n) ? acc + n : acc;
    }, 0);
  }, [improvements]);

  function openAdd() {
    setModalState({ open: true, editing: null });
  }
  function openEdit(imp) {
    setModalState({ open: true, editing: imp });
  }
  function closeModal() {
    setModalState({ open: false, editing: null });
  }

  // Modal calls this on save. Returns { error } so the modal can display.
  async function handleSave(payload, editingId) {
    if (editingId) {
      const { data, error } = await updateImprovement({ id: editingId, ...payload });
      if (error) return { error };
      // Replace the row in place.
      setImprovements((prev) =>
        prev.map((r) => (r.id === editingId ? data.improvement : r))
      );
    } else {
      const { data, error } = await createImprovement({ lead_id: leadId, ...payload });
      if (error) return { error };
      // Insert + re-sort by asset_id ASC (matches server ordering).
      setImprovements((prev) => {
        const next = [...prev, data.improvement];
        next.sort((a, b) => (a.asset_id || "").localeCompare(b.asset_id || ""));
        return next;
      });
    }
    return { error: null };
  }

  async function handleDelete(imp) {
    const label = imp.asset_id ? `${imp.asset_id} — ${imp.description}` : imp.description;
    const ok = window.confirm(`Delete ${label}? This can't be undone.`);
    if (!ok) return;
    setDeletingId(imp.id);
    const { error } = await deleteImprovement(imp.id);
    setDeletingId(null);
    if (error) {
      setPageError(error.message || "Could not delete that improvement.");
      return;
    }
    setImprovements((prev) => prev.filter((r) => r.id !== imp.id));
  }

  // ---- render ----
  if (redirectMessage) {
    return <Navigate to="/leads" replace state={{ errorMessage: redirectMessage }} />;
  }
  if (loading || !leaseTerms) {
    return (
      <div style={page}>
        <div style={container}>
          <p style={{ color: "#6b7280", fontSize: 16 }}>Loading improvements…</p>
        </div>
      </div>
    );
  }

  const addrDisplay =
    [leaseTerms?.address, leaseTerms?.city, leaseTerms?.state, leaseTerms?.zip]
      .filter(Boolean)
      .join(", ") || "this lead";

  return (
    <div style={page}>
      <div style={container}>
        {/* Header */}
        <div style={{ ...headerRow, marginBottom: 18 }}>
          <div>
            <h1 style={{ fontSize: 32, fontWeight: 800, color: "#111827", margin: "0 0 4px" }}>
              Improvements
            </h1>
            <p style={{ color: "#6b7280", fontSize: 15, margin: 0 }}>
              Capital improvements for {addrDisplay}
            </p>
          </div>
          <button type="button" style={secondaryButton} onClick={() => navigate("/leads")}>
            ← Back to Leads
          </button>
        </div>

        {/* Total improvement value — the headline number from the master lease §7.2 */}
        <div style={{ ...card, marginBottom: 18 }}>
          <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
            Total improvement value (book)
          </div>
          <div style={{ fontSize: 36, fontWeight: 800, color: "#111827", marginBottom: 8 }}>
            {usd(totalCurrentValue)}
          </div>
          <p style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.5, margin: 0, maxWidth: 720 }}>
            Sum of remaining book value across all assets. Master lease §7.2 values
            improvements at the greater of FMV or this figure.
          </p>
        </div>

        {/* Error banner */}
        {pageError && (
          <div
            style={{
              ...card,
              border: "2px solid #ef4444",
              background: "#fff5f5",
              color: "#b91c1c",
              fontWeight: 600,
              marginBottom: 18,
            }}
          >
            {pageError}
          </div>
        )}

        {/* Add button + table */}
        <div style={{ ...card }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>
              {improvements.length === 0
                ? "No improvements yet."
                : `${improvements.length} ${improvements.length === 1 ? "improvement" : "improvements"}`}
            </div>
            <button type="button" style={primaryButton} onClick={openAdd}>
              + Add improvement
            </button>
          </div>

          {improvements.length === 0 ? (
            <EmptyState onAdd={openAdd} />
          ) : (
            <ImprovementsTable
              improvements={improvements}
              deletingId={deletingId}
              onEdit={openEdit}
              onDelete={handleDelete}
            />
          )}
        </div>
      </div>

      {modalState.open && (
        <ImprovementModal
          editing={modalState.editing}
          onClose={closeModal}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

// ---- ImprovementsTable ----
function ImprovementsTable({ improvements, deletingId, onEdit, onDelete }) {
  const th = {
    textAlign: "left",
    fontSize: 11,
    fontWeight: 700,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: 1,
    padding: "10px 12px",
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
  };
  const td = {
    padding: "12px",
    borderBottom: "1px solid #f3f4f6",
    fontSize: 14,
    color: "#111827",
    verticalAlign: "top",
  };

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
        <thead>
          <tr>
            <th style={th}>Asset</th>
            <th style={th}>Description</th>
            <th style={th}>Category</th>
            <th style={th}>Installed</th>
            <th style={{ ...th, textAlign: "right" }}>Cost</th>
            <th style={{ ...th, textAlign: "right" }}>Current value</th>
            <th style={{ ...th, textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {improvements.map((imp) => (
            <tr key={imp.id}>
              <td style={{ ...td, fontWeight: 700, whiteSpace: "nowrap" }}>{imp.asset_id || "—"}</td>
              <td style={td}>
                <div>{imp.description || "—"}</div>
                {imp.notes && (
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4, lineHeight: 1.5 }}>
                    {imp.notes}
                  </div>
                )}
              </td>
              <td style={td}>
                <span style={categoryChip}>{CATEGORY_LABEL[imp.category] || imp.category}</span>
              </td>
              <td style={{ ...td, whiteSpace: "nowrap" }}>{shortDate(imp.install_date)}</td>
              <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>{usd(imp.cost)}</td>
              <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap", fontWeight: 700, color: imp.current_value === null ? "#6b7280" : "#15803d" }}>
                {imp.current_value === null ? "—" : usd(imp.current_value)}
              </td>
              <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                <button
                  type="button"
                  style={{ ...editButton, marginRight: 6 }}
                  onClick={() => onEdit(imp)}
                  disabled={deletingId === imp.id}
                >
                  Edit
                </button>
                <button
                  type="button"
                  style={{
                    ...dangerButton,
                    opacity: deletingId === imp.id ? 0.6 : 1,
                    cursor: deletingId === imp.id ? "not-allowed" : "pointer",
                  }}
                  onClick={() => onDelete(imp)}
                  disabled={deletingId === imp.id}
                >
                  {deletingId === imp.id ? "Deleting…" : "Delete"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---- EmptyState ----
function EmptyState({ onAdd }) {
  return (
    <div style={{ textAlign: "center", padding: "40px 20px", color: "#6b7280" }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 6 }}>
        Nothing tracked yet
      </div>
      <p style={{ fontSize: 14, lineHeight: 1.6, maxWidth: 520, margin: "0 auto 18px" }}>
        Add each capital improvement (gravel pad, fire pit, utility pedestal…) so the
        master lease's Improvement Value calc reflects your basis.
      </p>
      <button type="button" style={primaryButton} onClick={onAdd}>
        + Add your first improvement
      </button>
    </div>
  );
}

// ---- ImprovementModal ----
// Single modal used for both add (editing === null) and edit (editing === improvement row).
function ImprovementModal({ editing, onClose, onSave }) {
  // When editing, hydrate from the row; numeric fields converted to strings
  // so controlled inputs behave (NaN-on-empty pattern).
  const [form, setForm] = useState(() => ({
    asset_id:          editing?.asset_id          ?? "",
    description:       editing?.description       ?? "",
    category:          editing?.category          ?? "site_prep",
    install_date:      editing?.install_date      ?? "",
    cost:              editing?.cost              === undefined || editing?.cost === null ? "" : String(editing.cost),
    useful_life_years: editing?.useful_life_years === undefined || editing?.useful_life_years === null ? "" : String(editing.useful_life_years),
    salvage_value:     editing?.salvage_value     === undefined || editing?.salvage_value === null ? "" : String(editing.salvage_value),
    notes:             editing?.notes             ?? "",
  }));
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState("");

  function handleField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function num(v) {
    return v === "" || v === null || v === undefined ? null : Number(v);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setModalError("");

    // Client-side validation that mirrors the server's 400 paths.
    if (!form.asset_id.trim())    return setModalError("Asset ID is required.");
    if (!form.description.trim()) return setModalError("Description is required.");
    if (!form.category)           return setModalError("Category is required.");
    const cost = num(form.cost);
    if (cost === null || !Number.isFinite(cost)) return setModalError("Cost is required (numeric).");
    const life = num(form.useful_life_years);
    if (life === null || !Number.isFinite(life) || life <= 0) return setModalError("Useful life (years) must be a positive number.");

    setSubmitting(true);
    const payload = {
      asset_id:          form.asset_id.trim(),
      description:       form.description.trim(),
      category:          form.category,
      install_date:      form.install_date.trim() || null,
      cost,
      useful_life_years: Math.trunc(life),
      salvage_value:     num(form.salvage_value) ?? 0,
      notes:             form.notes.trim() || null,
    };
    const { error } = await onSave(payload, editing?.id);
    setSubmitting(false);
    if (error) {
      setModalError(error.message || "Could not save improvement.");
      return;
    }
    onClose();
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(17, 24, 39, 0.6)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "40px 20px",
        zIndex: 9999,
        overflowY: "auto",
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        style={{ ...card, width: "100%", maxWidth: 560, padding: 28 }}
      >
        <div style={{ ...headerRow, marginBottom: 18 }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: "#111827", margin: 0 }}>
            {editing ? "Edit improvement" : "Add improvement"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent", border: "none", color: "#6b7280",
              fontSize: 22, cursor: "pointer", lineHeight: 1,
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {modalError && (
          <div
            style={{
              border: "1px solid #fecaca",
              background: "#fef2f2",
              color: "#b91c1c",
              padding: "10px 12px",
              borderRadius: 10,
              marginBottom: 14,
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {modalError}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12, marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>Asset ID *</label>
            <input
              style={inputStyle}
              value={form.asset_id}
              onChange={(e) => handleField("asset_id", e.target.value)}
              placeholder="FC-001"
            />
          </div>
          <div>
            <label style={labelStyle}>Description *</label>
            <input
              style={inputStyle}
              value={form.description}
              onChange={(e) => handleField("description", e.target.value)}
              placeholder="Gravel pad installation"
            />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>Category *</label>
            <select
              style={inputStyle}
              value={form.category}
              onChange={(e) => handleField("category", e.target.value)}
            >
              {CATEGORY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Install date</label>
            <input
              style={inputStyle}
              type="date"
              value={form.install_date || ""}
              onChange={(e) => handleField("install_date", e.target.value)}
            />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>Cost ($) *</label>
            <input
              style={inputStyle}
              value={form.cost}
              onChange={(e) => handleField("cost", e.target.value)}
              inputMode="numeric"
              placeholder="10000"
            />
          </div>
          <div>
            <label style={labelStyle}>Useful life (yr) *</label>
            <input
              style={inputStyle}
              value={form.useful_life_years}
              onChange={(e) => handleField("useful_life_years", e.target.value)}
              inputMode="numeric"
              placeholder="10"
            />
          </div>
          <div>
            <label style={labelStyle}>Salvage ($)</label>
            <input
              style={inputStyle}
              value={form.salvage_value}
              onChange={(e) => handleField("salvage_value", e.target.value)}
              inputMode="numeric"
              placeholder="0"
            />
          </div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Notes</label>
          <textarea
            style={{ ...inputStyle, minHeight: 80, resize: "vertical", fontFamily: "inherit" }}
            value={form.notes}
            onChange={(e) => handleField("notes", e.target.value)}
            placeholder="Anything worth remembering about this asset…"
          />
        </div>

        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button type="button" onClick={onClose} style={secondaryButton} disabled={submitting}>
            Cancel
          </button>
          <button
            type="submit"
            style={{
              ...primaryButton,
              opacity: submitting ? 0.7 : 1,
              cursor: submitting ? "not-allowed" : "pointer",
            }}
            disabled={submitting}
          >
            {submitting ? "Saving…" : editing ? "Save changes" : "Add improvement"}
          </button>
        </div>
      </form>
    </div>
  );
}
